import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BrandedCard, BrandedButton, BootPulse } from '../components';
import { useBranding } from '../hooks/useBranding';
import { hydraService, ConsentRequest } from '../services/hydraService';
import './Consent.css';

export const Consent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { loading: brandingLoading } = useBranding();

  const [consentRequest, setConsentRequest] = useState<ConsentRequest | null>(null);
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const challenge = searchParams.get('consent_challenge');

  useEffect(() => {
    const loadConsentRequest = async () => {
      if (!challenge) {
        setError('Missing consent challenge parameter');
        setLoading(false);
        return;
      }

      try {
        const request = await hydraService.getConsentRequest(challenge);
        setConsentRequest(request);

        // If skip is true, automatically accept with all requested scopes
        if (request.skip) {
          const response = await hydraService.acceptConsent(challenge, request.requested_scope);
          window.location.href = response.redirect_to;
          return;
        }

        // Pre-select all requested scopes
        setSelectedScopes(new Set(request.requested_scope));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load consent request');
      } finally {
        setLoading(false);
      }
    };

    loadConsentRequest();
  }, [challenge]);

  const toggleScope = (scope: string) => {
    const newScopes = new Set(selectedScopes);
    if (newScopes.has(scope)) {
      newScopes.delete(scope);
    } else {
      newScopes.add(scope);
    }
    setSelectedScopes(newScopes);
  };

  const handleAccept = async () => {
    if (!challenge || !consentRequest) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await hydraService.acceptConsent(
        challenge,
        Array.from(selectedScopes),
        remember
      );

      // Redirect to the URL provided by Hydra
      window.location.href = response.redirect_to;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept consent');
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!challenge) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await hydraService.rejectConsent(
        challenge,
        'The user denied the consent request'
      );

      // Redirect to the URL provided by Hydra
      window.location.href = response.redirect_to;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject consent');
      setSubmitting(false);
    }
  };

  const getScopeDescription = (scope: string): string => {
    const descriptions: Record<string, string> = {
      openid: 'Access your basic profile information',
      profile: 'Access your full profile (name, picture, etc.)',
      email: 'Access your email address',
      phone: 'Access your phone number',
      address: 'Access your address information',
      offline_access: 'Access your data while you are offline',
    };

    return descriptions[scope] || `Access to ${scope}`;
  };

  if (loading || brandingLoading) {
    return (
      <BrandedCard>
        <BootPulse label="Loading consent request" />
      </BrandedCard>
    );
  }

  if (error && !consentRequest) {
    return (
      <BrandedCard title="Error">
        <div className="consent-error" role="alert">
          {error}
        </div>
      </BrandedCard>
    );
  }

  if (!consentRequest) {
    return (
      <BrandedCard title="Error">
        <div className="consent-error" role="alert">
          Invalid consent request
        </div>
      </BrandedCard>
    );
  }

  return (
    <BrandedCard title="Authorization Request">
      <div className="consent-container">
        {error && (
          <div className="consent-error" role="alert">
            {error}
          </div>
        )}

        <div className="consent-client-info">
          {consentRequest.client.logo_uri && (
            <img
              src={consentRequest.client.logo_uri}
              alt={consentRequest.client.client_name}
              className="consent-client-logo"
            />
          )}
          <p className="consent-message">
            <strong>{consentRequest.client.client_name}</strong> is requesting access to your account.
          </p>
        </div>

        <div className="consent-scopes">
          <h3 className="consent-section-title">Permissions Requested:</h3>
          <div className="consent-scope-list">
            {consentRequest.requested_scope.map((scope) => (
              <label key={scope} className="consent-scope-item">
                <input
                  type="checkbox"
                  checked={selectedScopes.has(scope)}
                  onChange={() => toggleScope(scope)}
                  className="consent-checkbox"
                />
                <div className="consent-scope-details">
                  <span className="consent-scope-name">{scope}</span>
                  <span className="consent-scope-description">
                    {getScopeDescription(scope)}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="consent-remember">
          <label className="consent-remember-label">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="consent-checkbox"
            />
            <span>Remember my choice for 1 hour</span>
          </label>
        </div>

        <div className="consent-actions">
          <BrandedButton
            onClick={handleAccept}
            variant="primary"
            fullWidth
            disabled={submitting || selectedScopes.size === 0}
          >
            {submitting ? 'Processing...' : 'Allow Access'}
          </BrandedButton>

          <BrandedButton
            onClick={handleReject}
            variant="outline"
            fullWidth
            disabled={submitting}
          >
            Deny
          </BrandedButton>
        </div>

        {consentRequest.client.policy_uri && (
          <div className="consent-links">
            <a
              href={consentRequest.client.policy_uri}
              target="_blank"
              rel="noopener noreferrer"
              className="consent-link"
            >
              Privacy Policy
            </a>
          </div>
        )}

        {consentRequest.client.tos_uri && (
          <div className="consent-links">
            <a
              href={consentRequest.client.tos_uri}
              target="_blank"
              rel="noopener noreferrer"
              className="consent-link"
            >
              Terms of Service
            </a>
          </div>
        )}
      </div>
    </BrandedCard>
  );
};
